/**
 * UI state that is not engine data (web.md §3): list filters, search text, the dialog stack, the settings
 * draft + dirty flag, the phone screen and the toast. One `useSyncExternalStore` store; no state library.
 * `UiState` (details open, sidebar collapsed, last view) is persisted through `settings.setLocal('ui')`
 * by app/usePersistedUi.ts.
 */

import { useSyncExternalStore } from 'react';
import type { SourceId, TopicId } from '@digester/core';

export interface ListFilter {
  unreadOnly: boolean;
  sourceId: SourceId | null;
  topicId: TopicId | null;
  sort: 'newest' | 'relevance';
  /** Search every folder instead of the open one. */
  allFolders: boolean;
}

export const DEFAULT_LIST_FILTER: ListFilter = { unreadOnly: false, sourceId: null, topicId: null, sort: 'newest', allFolders: false };

/** One open dialog: a registered kind plus its props (app/dialogs.tsx). */
export interface DialogEntry {
  id: number;
  kind: string;
  props: Record<string, unknown>;
}

/** The settings editor's draft, registered by `useDraft` (settings/shell/draft.ts). */
export interface DraftState {
  dirty: boolean;
  isNew: boolean;
  /** Validate and save; resolves false when the form is invalid or the engine refused. */
  save(): Promise<boolean>;
  /** Drop the changes. */
  discard(): void;
}

export interface ToastState {
  id: number;
  text: string;
  /** The undo ids of the mutations behind this toast, in the order they were made (Undo reverses). */
  undoIds: string[];
}

/** Phone screens (web.md §5): the workspace gets `mobile-<screen>`. */
export type MobileOverlay = 'none' | 'navigation' | 'details';

export interface UiStoreState {
  search: string;
  settingsSearch: string;
  listFilter: ListFilter;
  settingsEnabledOnly: boolean;
  dialogs: DialogEntry[];
  draft: DraftState | null;
  toast: ToastState | null;
  /** Phone only: the navigation or Details screen shown over the route's screen. */
  mobileOverlay: MobileOverlay;
  detailsOpen: boolean;
  sidebarCollapsed: boolean;
}

const initial: UiStoreState = {
  search: '',
  settingsSearch: '',
  listFilter: DEFAULT_LIST_FILTER,
  settingsEnabledOnly: false,
  dialogs: [],
  draft: null,
  toast: null,
  mobileOverlay: 'none',
  detailsOpen: true,
  sidebarCollapsed: false,
};

let state: UiStoreState = initial;
const listeners = new Set<() => void>();

export const ui = {
  get(): UiStoreState {
    return state;
  },
  set(patch: Partial<UiStoreState> | ((s: UiStoreState) => Partial<UiStoreState>)): void {
    const next = typeof patch === 'function' ? patch(state) : patch;
    state = { ...state, ...next };
    for (const listener of [...listeners]) listener();
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
  /** Tests and sign-out. */
  reset(): void {
    state = initial;
    for (const listener of [...listeners]) listener();
  },
};

/** Select a slice of the UI store; re-renders only when the slice changes (Object.is). */
export function useUi<T>(select: (s: UiStoreState) => T): T {
  return useSyncExternalStore(ui.subscribe, () => select(state), () => select(state));
}
